// src/data/spaceFitKnobsClient.js
//
// SPACE-FIT-BENCHMARKS-S2: Client for Space Fit formula knob endpoints.
// SPACE-FIT-EXPANDABLE-S2: Added overSeatedFloor and minHeadroomPct fields.
//
//   GET /api/admin/space-fit-knobs   — fetch current knobs
//   PUT /api/admin/space-fit-knobs   — update knobs (admin only)

import {
  AuthError,
  NetworkError,
  ValidationError,
  errorFromResponse,
} from "./errors.js";
import { log } from "../utils/logger.js";

async function jsonRequest(url, init) {
  let res;
  try {
    res = await fetch(url, {
      ...init,
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
    });
  } catch (err) {
    log.error("spaceFitKnobsClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("spaceFitKnobsClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  if (res.status === 204) return null;
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

/**
 * Fetch the current Space Fit formula knobs.
 * @returns {Promise<{
 *   avgTarget: number,
 *   peakComfort: number,
 *   peakCeiling: number,
 *   overSeatedFloor: number,
 *   minHeadroomPct: number
 * }>}
 */
export async function getSpaceFitKnobs() {
  return jsonRequest("/api/admin/space-fit-knobs");
}

/**
 * Update the Space Fit formula knobs (admin only).
 * @param {{
 *   avgTarget: number,
 *   peakComfort: number,
 *   peakCeiling: number,
 *   overSeatedFloor: number,
 *   minHeadroomPct: number
 * }} knobs
 * @returns {Promise<{
 *   avgTarget: number,
 *   peakComfort: number,
 *   peakCeiling: number,
 *   overSeatedFloor: number,
 *   minHeadroomPct: number
 * }>}
 */
export async function putSpaceFitKnobs({ avgTarget, peakComfort, peakCeiling, overSeatedFloor, minHeadroomPct }) {
  return jsonRequest("/api/admin/space-fit-knobs", {
    method: "PUT",
    body: JSON.stringify({ avgTarget, peakComfort, peakCeiling, overSeatedFloor, minHeadroomPct }),
  });
}
